'use client';

import { DISTANCE_UNITS } from '../../constants/constants';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '../../helpers/hooks/useScreenWidth';
import { Button } from '../Clickables/Buttons/Button';
import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { NearbyPlaceEntry } from './components/NearbyPlaceEntry';

import { useState } from 'react';

export interface INearbyPlace {
  name: string;
  distance: number;
  url: string;
}

interface IProps {
  id?: string;
  distanceUnit: (typeof DISTANCE_UNITS)[keyof typeof DISTANCE_UNITS];
  nearbyPlaces: INearbyPlace[];
  mainLatitude: number;
  mainLongitude: number;
  title?: string;
}

const SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE = 6;
const SHOW_NUMBER_OF_NEARBY_PLACES_TABLET = 9;

/**
 * NearbyPlacesSection component displays a list of nearby places with their distances from a main Location (lng/lat).
 * It shows a limited number of places on mobile and tablet screens, with an option to show more.
 *
 * @param {string} [id='nearby-places'] - The id of the container element.
 * @param {DISTANCE_UNITS} [distanceUnit=DISTANCE_UNITS.MILES] - The unit of distance to display.
 * @param {INearbyPlace[]} nearbyPlaces - An array of nearby places to display.
 * @param {number} mainLatitude - The latitude of the main location.
 * @param {number} mainLongitude - The longitude of the main location.
 * @param {string} [title='Cities nearby'] - The title of the section.
 *
 * @returns {JSX.Element} The NearbyPlacesSection component.
 */
export const NearbyPlacesSection = ({
  id = 'nearby-places',
  distanceUnit = DISTANCE_UNITS.MILES,
  nearbyPlaces,
  title = 'Cities nearby',
}: IProps) => {
  const [showAll, setShowAll] = useState(false);

  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();

  const showMoreButtonVisible =
    (isMobileScreenWidthOrThinner && nearbyPlaces.length > SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE) ||
    (isTabletScreenWidthOrThinner && nearbyPlaces.length > SHOW_NUMBER_OF_NEARBY_PLACES_TABLET);

  const handleShowMoreClick = () => {
    setShowAll(true);
  };

  return (
    <Container
      id={id}
      className="mb-fw scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
    >
      <div className="bg-lake-fog flex flex-col items-start justify-center gap-6 rounded-lg p-6 sm:p-8 md:p-10">
        <Heading as="h2" variant="sm" className="mb-0">
          {title}
        </Heading>
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {nearbyPlaces.map(({ name, distance, url }, index) => {
            if (!name || !distance || !url) {
              return <></>;
            }
            const isHidden =
              (isMobileScreenWidthOrThinner && index >= SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE) ||
              (isTabletScreenWidthOrThinner && index >= SHOW_NUMBER_OF_NEARBY_PLACES_TABLET);

            return (
              <NearbyPlaceEntry
                distance={distance}
                distanceUnit={distanceUnit}
                placeName={name}
                placeUrl={url}
                key={name}
                isHidden={!showAll && isHidden}
              />
            );
          })}
        </div>
        {showMoreButtonVisible && !showAll && (
          <Button
            size="sm"
            className="mt-2 w-full p-2"
            variant="textPrimary"
            onClick={handleShowMoreClick}
          >
            Show more cities
          </Button>
        )}
      </div>
    </Container>
  );
};
